import { MessageSquare, PanelRightClose, PanelRightOpen, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useIsMobile, useIsTablet } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import CommentsPanel, { Comment } from "@/components/demo/CommentsPanel";

interface CommentsSidebarProps {
  comments: Comment[];
  highlightedCommentId: string | null;
  onCommentHover?: (commentId: string | null) => void;
  onCommentClick?: (commentId: string) => void;
  onAddComment?: (content: string) => void;
  onDeleteComment?: (commentId: string) => void;
  currentUserId?: string;
  stakeholderToken?: string;
  isReadOnly?: boolean;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  variationTag?: string | null;
}

export function CommentsSidebar({
  comments,
  highlightedCommentId,
  onCommentHover,
  onCommentClick,
  onAddComment,
  onDeleteComment,
  currentUserId,
  stakeholderToken,
  isReadOnly = false,
  isOpen,
  onOpenChange,
  variationTag,
}: CommentsSidebarProps) {
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();

  const panelContent = (
    <div className="p-6 bg-card">
      <CommentsPanel
        comments={comments}
        highlightedCommentId={highlightedCommentId}
        onCommentHover={onCommentHover}
        onCommentClick={onCommentClick}
        onAddComment={onAddComment}
        onDeleteComment={onDeleteComment}
        currentUserId={currentUserId}
        stakeholderToken={stakeholderToken}
        isReadOnly={isReadOnly}
        variationTag={variationTag}
      />
    </div>
  );

  // Mobile: horizontal bottom bar that slides up
  if (isMobile) {
    return (
      <>
        {/* Bottom bar - always visible when collapsed */}
        {!isOpen && (
          <div 
            className="absolute bottom-0 left-0 right-0 h-14 bg-card border-t border-foreground/10 flex items-center justify-between px-4 cursor-pointer z-30 animate-fade-in"
            onClick={() => onOpenChange(true)}
          >
            <div className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-muted-foreground" />
              <span className="text-sm font-medium">Comments</span>
              {comments.length > 0 && (
                <Badge variant="secondary" className="h-5 min-w-5 flex items-center justify-center text-xs">
                  {comments.length}
                </Badge>
              )}
            </div>
            <ChevronUp className="w-5 h-5 text-muted-foreground" />
          </div>
        )}
        
        {/* Full panel - slides up when expanded */}
        {isOpen && (
          <div className="absolute inset-0 bg-card z-40 flex flex-col overflow-auto animate-slide-up">
            <div className="p-6 bg-card">
              <CommentsPanel
                comments={comments}
                highlightedCommentId={highlightedCommentId}
                onCommentHover={onCommentHover}
                onCommentClick={onCommentClick}
                onAddComment={onAddComment}
                onDeleteComment={onDeleteComment}
                currentUserId={currentUserId}
                stakeholderToken={stakeholderToken}
                isReadOnly={isReadOnly}
                isCollapsible={true}
                onToggle={() => onOpenChange(false)}
                isMobileExpanded={true}
                variationTag={variationTag}
              />
            </div>
          </div>
        )}
      </>
    );
  }

  // Tablet: overlay sidebar (50% width, positioned absolutely)
  if (isTablet) {
    return (
      <>
        {/* Collapsed bar - always in flow */}
        <div 
          className={cn(
            "flex flex-col border-l border-foreground/10 bg-card transition-all duration-200 ease-linear",
            isOpen ? "w-0 overflow-hidden" : "w-12"
          )}
        >
          <div 
            className="flex flex-col items-center py-6 cursor-pointer hover:bg-muted/50 transition-colors h-full"
            onClick={() => onOpenChange(true)}
          >
            <MessageSquare className="w-5 h-5 text-muted-foreground" />
            {comments.length > 0 && (
              <Badge variant="secondary" className="mt-2 h-5 min-w-5 flex items-center justify-center text-xs">
                {comments.length}
              </Badge>
            )}
          </div>
        </div>
        
        {/* Overlay panel when expanded */}
        {isOpen && (
          <div className="absolute right-0 top-0 bottom-0 w-1/2 bg-card border-l border-foreground/10 z-40 shadow-xl overflow-auto animate-slide-in-right">
            <div className="p-6 bg-card">
              <CommentsPanel
                comments={comments}
                highlightedCommentId={highlightedCommentId}
                onCommentHover={onCommentHover}
                onCommentClick={onCommentClick}
                onAddComment={onAddComment}
                onDeleteComment={onDeleteComment}
                currentUserId={currentUserId}
                stakeholderToken={stakeholderToken}
                isReadOnly={isReadOnly}
                isCollapsible={true}
                onToggle={() => onOpenChange(false)}
                variationTag={variationTag}
              />
            </div>
          </div>
        )}
      </>
    );
  }

  // Desktop: inline sidebar that doesn't use fixed positioning
  return (
    <div
      className={cn(
        "hidden lg:flex lg:flex-col border-l border-foreground/10 bg-card transition-all duration-200 ease-linear overflow-hidden min-h-0",
        isOpen ? "w-[380px]" : "w-12"
      )}
    >
      {!isOpen ? (
        // Collapsed: thin bar with icon and badge
        <div 
          className="flex flex-col items-center py-6 cursor-pointer hover:bg-muted/50 transition-colors h-full"
          onClick={() => onOpenChange(true)}
        >
          <MessageSquare className="w-5 h-5 text-muted-foreground" />
          {comments.length > 0 && (
            <Badge variant="secondary" className="mt-2 h-5 min-w-5 flex items-center justify-center text-xs">
              {comments.length}
            </Badge>
          )}
        </div>
      ) : (
        // Expanded: full panel with toggle in header
        <div className="flex-1 overflow-auto">
          <div className="p-6 bg-card">
            <CommentsPanel
              comments={comments}
              highlightedCommentId={highlightedCommentId}
              onCommentHover={onCommentHover}
              onCommentClick={onCommentClick}
              onAddComment={onAddComment}
              onDeleteComment={onDeleteComment}
              currentUserId={currentUserId}
              stakeholderToken={stakeholderToken}
              isReadOnly={isReadOnly}
              isCollapsible={true}
              onToggle={() => onOpenChange(false)}
              variationTag={variationTag}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// Standalone trigger for use outside SidebarProvider
interface CommentsSidebarTriggerProps {
  commentCount: number;
  isOpen: boolean;
  onToggle: () => void;
}

export function CommentsSidebarTrigger({ commentCount, isOpen, onToggle }: CommentsSidebarTriggerProps) {
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={onToggle}
      className="flex items-center gap-2"
    >
      {isOpen ? (
        <PanelRightClose className="w-4 h-4" />
      ) : (
        <PanelRightOpen className="w-4 h-4" />
      )}
      <MessageSquare className="w-4 h-4" />
      {commentCount > 0 && (
        <Badge variant="secondary" className="ml-1 h-5 min-w-5 flex items-center justify-center text-xs">
          {commentCount}
        </Badge>
      )}
      <span className="hidden sm:inline">Comments</span>
    </Button>
  );
}
